import React, { useEffect, useState, useRef } from "react";
import { useSocket } from "../../../../context/SocketContext";
import { Pin, CheckCheck, Check, Clock, Mic } from "lucide-react";
import GroupIcon from "@mui/icons-material/Group";

export default function ChatListItem({
  chat,
  currentUserId,
  currentChat,
  onClick,
  isApproved,
}) {
  const { socket, connectionStatus, isUserOnline } = useSocket();

  const [isOnline, setIsOnline] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [lastMessage, setLastMessage] = useState(chat?.latestMessage);
  const [lastSeen, setLastSeen] = useState(null);
  const [messageStatus, setMessageStatus] = useState(null);
  const [isVoiceMessage, setIsVoiceMessage] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  const BASE_DOWNLOAD_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;

  const typingTimeoutRef = useRef(null);
  const notificationTimeoutRef = useRef(null);

  const other = chat?.users?.find((u) => u._id !== currentUserId);
  const isGroup = chat?.isGroupChat;

  const getCurrentUserUnreadCount = () => {
    if (!Array.isArray(chat?.unreadCounts)) return 0;

    const entry = chat.unreadCounts.find(
      (u) => u.user.toString() === currentUserId.toString()
    );
    return entry ? entry.count : 0;
  };

  const [unreadCount, setUnreadCount] = useState(getCurrentUserUnreadCount());

  useEffect(() => {
    const newUnreadCount = getCurrentUserUnreadCount();
    setUnreadCount(newUnreadCount);
  }, [chat, currentUserId, chat?.unreadCounts]);

  useEffect(() => {
    if (chat) {
      const chatDataUnreadCount = getCurrentUserUnreadCount();

      setUnreadCount(chatDataUnreadCount);
      setLastMessage(chat.latestMessage);
      setIsOnline(other?._id ? isUserOnline(other._id) : false);

      // ✅ Fixed: Check if current user sent the message and if it's been read by others
      if (chat.latestMessage?.sender?._id === currentUserId) {
        const readBy = chat.latestMessage?.readBy || [];

        // Check if any OTHER user has read the message
        const isReadByOthers = readBy.some((readUser) => {
          const readUserId =
            typeof readUser === "string" ? readUser : readUser._id;
          return readUserId !== currentUserId;
        });

        if (isReadByOthers) {
          setMessageStatus("seen");
        } else if (
          chat.latestMessage.delivered ||
          chat.latestMessage.deliveredTo?.length > 0
        ) {
          setMessageStatus("delivered");
        } else {
          setMessageStatus(chat.latestMessage.status || "sent");
        }
      }

      // Check if it's a voice message from files array or old fileType
      const hasAudioFile = chat.latestMessage?.files?.some(
        (file) => file.type === "audio"
      );
      const isOldAudioType =
        chat.latestMessage?.fileType === "audio" ||
        chat.latestMessage?.messageType === "voice";

      setIsVoiceMessage(hasAudioFile || isOldAudioType);
    }
  }, [chat, isUserOnline, other?._id, currentUserId]);

  useEffect(() => {
    if (chat._id !== currentChat) {
      setIsTyping(false);
      setTypingUsers(new Set());
      setHasNewMessage(false);

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    }
  }, [currentChat, chat._id]);

  useEffect(() => {
    if (!socket) return;

    if (other?._id) {
      socket.emit("checkUserOnline", other._id);
    }

    const handleUserStatusUpdate = ({ userId, isOnline: online, lastSeen }) => {
      if (userId === other?._id) {
        setIsOnline(online);
        if (lastSeen) setLastSeen(new Date(lastSeen));
      }
    };

    const handleUserOnline = ({ userId }) => {
      if (userId === other?._id) {
        setIsOnline(true);
        setLastSeen(new Date());
      }
    };

    const handleUserOffline = ({ userId, lastSeen }) => {
      if (userId === other?._id) {
        setIsOnline(false);
        if (lastSeen) setLastSeen(new Date(lastSeen));
      }
    };

    const handleMessageRead = ({ chatId, userId: readerId }) => {
      if (chatId === chat._id) {
        if (readerId === currentUserId) {
          setUnreadCount(0);
          setHasNewMessage(false);
        } else if (
          readerId === other?._id ||
          (isGroup && readerId !== currentUserId)
        ) {
          setMessageStatus("seen");
        }
      }
    };

    const handleMessageDelivered = ({ chatId, deliveredTo }) => {
      if (chatId === chat._id && Array.isArray(deliveredTo)) {
        if (isGroup) {
          const otherUsers =
            chat.users?.filter((u) => u._id !== currentUserId) || [];
          const deliveredToOthers = otherUsers.some((user) =>
            deliveredTo.includes(user._id)
          );
          if (deliveredToOthers) {
            setMessageStatus("delivered");
          }
        } else if (other?._id && deliveredTo.includes(other._id)) {
          setMessageStatus("delivered");
        }
      }
    };

    const handleMessageSeen = ({ chatId, seenBy, messageId }) => {
      if (chatId === chat._id && Array.isArray(seenBy)) {
        const seenByOthers = seenBy.some((userId) => userId !== currentUserId);
        if (seenByOthers) {
          setMessageStatus("seen");
        }
      }
    };

    const handleUserTyping = ({ chatId, userId, username }) => {
      if (chatId === chat._id && userId !== currentUserId) {
        setTypingUsers((prev) => new Set([...prev, { userId, username }]));
        setIsTyping(true);

        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
          setTypingUsers(new Set());
        }, 5000);
      }
    };

    const handleUserStoppedTyping = ({ chatId, userId }) => {
      if (chatId === chat._id && userId !== currentUserId) {
        setTypingUsers((prev) => {
          const newSet = new Set(
            [...prev].filter((user) => user.userId !== userId)
          );
          if (newSet.size === 0) {
            setIsTyping(false);
          }
          return newSet;
        });
      }
    };

    const handleNewMessage = (message) => {
      if (message.chatId === chat._id || message.chat === chat._id) {
        setLastMessage(message);
        setIsTyping(false);
        setTypingUsers(new Set());

        // Check for voice message in new files array structure
        const hasAudioFile = message.files?.some(
          (file) => file.type === "audio"
        );
        const isOldAudioType =
          message.fileType === "audio" || message.messageType === "voice";

        setIsVoiceMessage(hasAudioFile || isOldAudioType);

        if (message.sender?._id !== currentUserId && chat._id !== currentChat) {
          setHasNewMessage(true);
          setUnreadCount((prev) => prev + 1);

          if (notificationTimeoutRef.current)
            clearTimeout(notificationTimeoutRef.current);
          notificationTimeoutRef.current = setTimeout(
            () => setHasNewMessage(false),
            3000
          );
        } else if (message.sender?._id === currentUserId) {
          setMessageStatus("sent");
        }
      }
    };

    socket.on("userStatusUpdate", handleUserStatusUpdate);
    socket.on("userOnline", handleUserOnline);
    socket.on("userOffline", handleUserOffline);
    socket.on("messageRead", handleMessageRead);
    socket.on("messageDelivered", handleMessageDelivered);
    socket.on("messageSeen", handleMessageSeen);
    socket.on("userTyping", handleUserTyping);
    socket.on("userStoppedTyping", handleUserStoppedTyping);
    socket.on("newMessage", handleNewMessage);

    return () => {
      socket.off("userStatusUpdate", handleUserStatusUpdate);
      socket.off("userOnline", handleUserOnline);
      socket.off("userOffline", handleUserOffline);
      socket.off("messageRead", handleMessageRead);
      socket.off("messageDelivered", handleMessageDelivered);
      socket.off("messageSeen", handleMessageSeen);
      socket.off("userTyping", handleUserTyping);
      socket.off("userStoppedTyping", handleUserStoppedTyping);
      socket.off("newMessage", handleNewMessage);

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (notificationTimeoutRef.current)
        clearTimeout(notificationTimeoutRef.current);
    };
  }, [
    socket,
    other?._id,
    chat?._id,
    currentUserId,
    currentChat,
    isGroup,
    chat.users,
  ]);

  if (!chat || !Array.isArray(chat.users)) return null;

  const formatLastMessageTime = () => {
    if (!lastMessage?.createdAt) return null;
    const d = new Date(lastMessage.createdAt);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const getInitials = (user) =>
    (user?.firstName?.[0] || "") + (user?.lastName?.[0] || "") || "?";

  const getTypingText = () => {
    const typingArray = Array.from(typingUsers);
    if (typingArray.length === 0) return "";

    if (isGroup) {
      if (typingArray.length === 1) {
        return `${typingArray[0].username || "Someone"} is ${
          isVoiceMessage ? "typing..." : "typing..."
        }`;
      } else {
        return `${isVoiceMessage ? "recording..." : "typing..."}`;
      }
    } else {
      return isVoiceMessage ? "typing..." : "typing...";
    }
  };

  const getLastMessagePreview = () => {
    if (!lastMessage) return "No messages yet";

    // ✅ Handle call data FIRST - WhatsApp style
    if (lastMessage.messageType === "call" && lastMessage.callData) {
      const { type, status, duration, initiator } = lastMessage.callData;
      const isOutgoing = initiator === currentUserId;

      // Format duration helper
      const formatDuration = (seconds) => {
        if (!seconds || seconds === 0) return null;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
      };

      let callIcon = "";
      let callPrefix = "";
      let callText = "";

      // Determine call direction prefix
      // if (isOutgoing) {
      //   callPrefix = "↗️ "; // Outgoing arrow
      // } else {
      //   callPrefix = "↙️ "; // Incoming arrow
      // }

      // Determine icon based on call type
      if (type === "video") {
        callIcon = "📹";
        callText = "Video call";
      } else {
        callIcon = "📞";
        callText = "Voice call";
      }

      // Handle different call statuses
      if (status === "missed" && !isOutgoing) {
        callIcon = "📵";
        callText = "Missed call";
        callPrefix = "";
      } else if (status === "cancelled") {
        callIcon = "📵";
        callText = "Cancelled call";
      } else if (status === "rejected") {
        if (isOutgoing) {
          callText = "Call declined";
        } else {
          callText = "Declined call";
        }
      } else if (status === "completed") {
        const durationStr = formatDuration(duration);
        if (durationStr) {
          callText += ` (${durationStr})`;
        }
      }

      return `${callPrefix}${callIcon} ${callText}`;
    }

    // Check for voice message in files array
    const hasAudioFile = lastMessage.files?.some(
      (file) => file.type === "audio"
    );
    if (hasAudioFile) return "🎤 Voice message";

    // Check old fileType for backward compatibility
    if (isVoiceMessage) return "🎤 Voice message";

    // Handle files array - check if multiple files or single file
    if (lastMessage.files && lastMessage.files.length > 0) {
      const files = lastMessage.files;

      // If multiple files, show count with appropriate icon
      if (files.length > 1) {
        const fileTypes = {
          image: files.filter((f) => f.type === "image").length,
          video: files.filter((f) => f.type === "video").length,
          document: files.filter((f) => f.type === "document").length,
          audio: files.filter((f) => f.type === "audio").length,
        };

        // Determine which type is most common
        if (fileTypes.image > 0 && fileTypes.image >= fileTypes.video) {
          return `📷 ${files.length} ${
            files.length === 1 ? "Photo" : "Photos"
          }`;
        } else if (fileTypes.video > 0) {
          return `🎥 ${files.length} ${
            files.length === 1 ? "Video" : "Videos"
          }`;
        } else if (fileTypes.document > 0) {
          return `📄 ${files.length} ${
            files.length === 1 ? "Document" : "Documents"
          }`;
        } else {
          return `📎 ${files.length} Files`;
        }
      }

      // Single file - show type
      const file = files[0];
      switch (file.type) {
        case "image":
          return "📷 Photo";
        case "video":
          return "🎥 Video";
        case "audio":
          return "🎵 Audio";
        case "document":
          return "📄 Document";
        default:
          return "📎 File";
      }
    }

    // Backward compatibility with old fileUrl structure
    if (lastMessage.fileType && lastMessage.fileType !== "none") {
      switch (lastMessage.fileType) {
        case "image":
          return "📷 Photo";
        case "video":
          return "🎥 Video";
        case "audio":
          return "🎵 Audio";
        case "document":
          return "📄 Document";
        default:
          return "📎 File";
      }
    }

    // Text message
    if (isGroup && lastMessage.sender) {
      const senderName =
        lastMessage.sender._id === currentUserId
          ? "You"
          : lastMessage.sender.username || "Unknown";
      const content = lastMessage.content || "Message";
      return `${senderName}: ${content}`;
    }

    return lastMessage.content || "Message";
  };

  const renderMessageStatus = () => {
    if (!messageStatus || lastMessage?.sender?._id !== currentUserId)
      return null;

    switch (messageStatus) {
      case "sent":
        return <Clock size={12} className="text-gray-400" />;
      case "delivered":
        return <Check size={12} className="text-gray-400" />;
      case "seen":
        return <CheckCheck size={12} className="text-blue-500" />;
      default:
        return null;
    }
  };

  const TypingDots = () => (
    <div className="flex items-center gap-1">
      <div className="flex gap-1">
        <div className="w-1 h-1 bg-green-500 rounded-full animate-bounce"></div>
        <div className="w-1 h-1 bg-green-500 rounded-full animate-bounce delay-150"></div>
        <div className="w-1 h-1 bg-green-500 rounded-full animate-bounce delay-300"></div>
      </div>
      {isVoiceMessage && (
        <Mic size={12} className="text-green-500 animate-pulse" />
      )}
    </div>
  );

  const isCurrentChat = chat._id === currentChat;
  const showUnreadBadge = unreadCount > 0 && !isCurrentChat;

  const displayName = isGroup
    ? chat.chatName || "Unnamed Group"
    : `${other?.firstName || ""} ${other?.lastName || ""}`.trim() ||
      "Unknown User";

  const renderAvatar = () => {
    if (isGroup) {
      return (
        <div className="w-full h-full rounded-full flex items-center justify-center bg-blue-400 text-white font-bold text-lg">
          <GroupIcon />
        </div>
      );
    }

    if (other?.profilePic) {
      const profilePicUrl = other.profilePic.startsWith("http")
        ? other.profilePic
        : `${BASE_DOWNLOAD_URL}${other.profilePic}`;

      return (
        <img
          src={profilePicUrl}
          alt={other?.username || "user"}
          className="w-full h-full rounded-full object-cover"
        />
      );
    }

    return (
      <div className="w-full h-full rounded-full flex items-center justify-center bg-blue-400 text-white font-bold text-sm">
        {getInitials(other)}
      </div>
    );
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 relative cursor-pointer group
        ${
          isCurrentChat
            ? "bg-blue-50 border-r-2 border-blue-500"
            : "hover:bg-gray-50"
        }
       
        ${!isApproved ? "opacity-75" : ""}`}
      onClick={onClick}
    >
      {/* Avatar */}
      <div className="relative w-12 h-12 flex-shrink-0">
        {renderAvatar()}

        {!isGroup && isApproved && (
          <div className="absolute bottom-0 -right-1">
            <div
              className={`w-3 h-3 rounded-full border-2 border-white ${
                isOnline ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              {connectionStatus === "connected" && isOnline && (
                <div className="w-full h-full bg-green-500 rounded-full animate-pulse"></div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Chat Info */}
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex justify-between items-center mb-1">
          <h6
            className={`font-medium text-sm truncate ${
              isApproved
                ? isCurrentChat
                  ? "text-blue-600"
                  : showUnreadBadge
                  ? "text-gray-900 font-semibold"
                  : "text-gray-900"
                : "text-gray-500"
            }`}
          >
            {displayName}
          </h6>

          {/* Time */}
          {formatLastMessageTime() && (
            <span
              className={`text-xs flex-shrink-0 ml-2 ${
                showUnreadBadge ? "text-gray-700 font-medium" : "text-gray-400"
              }`}
            >
              {formatLastMessageTime()}
            </span>
          )}
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isTyping ? (
              <>
                <TypingDots />
                <span className="text-xs text-green-600 font-medium truncate">
                  {getTypingText()}
                </span>
              </>
            ) : (
              <p
                className={`text-xs truncate ${
                  isApproved
                    ? showUnreadBadge
                      ? "text-gray-700 font-medium"
                      : isCurrentChat
                      ? "text-blue-600"
                      : "text-gray-600"
                    : "text-gray-500"
                }`}
              >
                {getLastMessagePreview()}
              </p>
            )}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {chat.pinned && (
              <Pin
                size={12}
                className={isCurrentChat ? "text-blue-400" : "text-gray-400"}
              />
            )}
            {renderMessageStatus()}
            {showUnreadBadge && (
              <span className="text-xs bg-red-500 text-white rounded-full px-1 py-0 min-w-[18px] text-center font-medium">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>

      {!isApproved && (
        <div className="absolute top-2 right-2">
          <div className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium border border-yellow-200">
            ⏳ Pending
          </div>
        </div>
      )}
    </div>
  );
}
